import { type RefObject, useCallback, useEffect, useState } from "react";

import { POSITION_STORAGE_KEY } from "./constants";

type Point = { x: number; y: number };

export type Size = { width: number; height: number };

const CLICK_THRESHOLD = 4;

// Distance to the screen edge the pill settles at
const MARGIN = 16;

const SETTLE_MS = 220;

function read(): Point | null {
  try {
    const raw = window.localStorage.getItem(POSITION_STORAGE_KEY);
    const point = raw ? (JSON.parse(raw) as Point) : null;

    return point && "number" === typeof point.x && "number" === typeof point.y
      ? point
      : null;
  } catch {
    return null;
  }
}

function clamp(point: Point, size: Size): Point {
  return {
    x: Math.min(Math.max(point.x, 0), window.innerWidth - size.width),
    y: Math.min(Math.max(point.y, 0), window.innerHeight - size.height),
  };
}

// Within this distance of an edge the pill is taken by it
const CORNER_REACH = 96;

export type Edge = "left" | "right" | "top" | "bottom";

// The edge the pill is closest to, where its folded dot sticks
export function nearestEdge(point: Point, size: Size): Edge {
  const distances: Record<Edge, number> = {
    left: point.x,
    right: window.innerWidth - size.width - point.x,
    top: point.y,
    bottom: window.innerHeight - size.height - point.y,
  };

  return (Object.keys(distances) as Edge[]).sort(
    (a, b) => distances[a] - distances[b],
  )[0];
}

// The nearest screen edge takes the pill on its axis, so it never sits in
// the middle of a text; dropped close to two edges at once, it takes the
// corner.
function snap(point: Point, size: Size): Point {
  const right = window.innerWidth - size.width - MARGIN;
  const bottom = window.innerHeight - size.height - MARGIN;
  const x = Math.min(Math.max(point.x, MARGIN), right);
  const y = Math.min(Math.max(point.y, MARGIN), bottom);
  const toLeft = x - MARGIN;
  const toRight = right - x;
  const toTop = y - MARGIN;
  const toBottom = bottom - y;
  const horizontal = Math.min(toLeft, toRight);
  const vertical = Math.min(toTop, toBottom);
  const snappedX = toLeft <= toRight ? MARGIN : right;
  const snappedY = toTop <= toBottom ? MARGIN : bottom;

  if (horizontal <= CORNER_REACH && vertical <= CORNER_REACH) {
    return { x: snappedX, y: snappedY };
  }

  return horizontal <= vertical ? { x: snappedX, y } : { x, y: snappedY };
}

// A fixed element the user can drag anywhere on screen. The position is
// kept per browser and pulled back on screen when the viewport shrinks.
// The pointer handlers tell a click from a drag by a small threshold. The
// element is measured when it exists; `estimate` serves before that, and
// the pill's fixed height and min-width make it exact on the first paint.
export function useDraggablePosition(
  element: RefObject<HTMLElement | null>,
  estimate: Size,
  onClick: () => void,
) {
  const measure = useCallback(
    (): Size =>
      element.current
        ? {
            width: element.current.offsetWidth,
            height: element.current.offsetHeight,
          }
        : estimate,
    [element, estimate],
  );

  const [position, setPosition] = useState<Point>(() =>
    clamp(
      read() ?? {
        x: window.innerWidth - estimate.width - MARGIN,
        y: window.innerHeight - estimate.height - MARGIN,
      },
      estimate,
    ),
  );
  // True while the pill glides to its edge, which is when it animates
  const [settling, setSettling] = useState(false);

  useEffect(() => {
    const onResize = () => setPosition((current) => clamp(current, measure()));
    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (0 !== event.button) {
        return;
      }

      const target = event.currentTarget;
      const startX = event.clientX;
      const startY = event.clientY;
      const origin = position;
      let dragged = false;
      target.setPointerCapture(event.pointerId);

      const onMove = (move: PointerEvent) => {
        const dx = move.clientX - startX;
        const dy = move.clientY - startY;

        if (!dragged && Math.hypot(dx, dy) < CLICK_THRESHOLD) {
          return;
        }

        dragged = true;
        setPosition(clamp({ x: origin.x + dx, y: origin.y + dy }, measure()));
      };

      const onUp = () => {
        target.removeEventListener("pointermove", onMove);
        target.removeEventListener("pointerup", onUp);
        target.removeEventListener("pointercancel", onUp);

        if (dragged) {
          setSettling(true);
          setTimeout(() => setSettling(false), SETTLE_MS);
          setPosition((current) => {
            const settled = snap(current, measure());

            try {
              window.localStorage.setItem(
                POSITION_STORAGE_KEY,
                JSON.stringify(settled),
              );
            } catch {
              // Storage can be blocked; the position just does not persist.
            }

            return settled;
          });
        } else {
          onClick();
        }
      };

      target.addEventListener("pointermove", onMove);
      target.addEventListener("pointerup", onUp);
      target.addEventListener("pointercancel", onUp);
    },
    [onClick, position, measure],
  );

  return { position, settling, onPointerDown, measure };
}
